/**
 * CZNA-1343 — "Popis importu": what an import does, in two waves.
 *
 * Nothing here is a hand-kept list. The stages and their order come from the backend
 * (`ImportPhase`, held to `ImportService.processImport` by `ImportPhaseOrderTest`) and the
 * vocabularies from the stored derivation rules; this component only words them. A stage
 * code without a text still renders, under its code, rather than disappearing.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Loading, MessageBanner, Modal, ModalFooter, } from '@folio/stripes/components';
import useImportPipeline from '../../hooks/useImportPipeline';
import { useVocabularyTypes } from '../../hooks/useVocabularyTypes';
import { formatDerivationSources } from '../../utils/formatDerivationSource';
var WAVES = ['DATA', 'POST_IMPORT'];
var CELL_STYLE = {
    textAlign: 'left',
    padding: '4px 8px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
};
/** The derive pass after an import runs exactly the rules with both flags on. */
export var runsAfterImport = function (rule) { return rule.enabled && rule.autoOnImport; };
var ImportPipelineModal = function (_a) {
    var open = _a.open, onClose = _a.onClose;
    var intl = useIntl();
    var _b = useImportPipeline(open), data = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    var vocabularyTypes = useVocabularyTypes({ enabled: open }).vocabularyTypes;
    var key = function (id) { return "ui-czna-authorities.imports.pipeline.".concat(id); };
    var msg = function (id, values) { return intl.formatMessage({ id: key(id) }, values); };
    var hasText = function (id) { var _a; return !!((_a = intl.messages) === null || _a === void 0 ? void 0 : _a[key(id)]); };
    var yesNo = function (value) { return msg(value ? 'yes' : 'no'); };
    var vocabularyLabel = function (code) {
        var _a;
        var type = vocabularyTypes.find(function (vt) { return vt.code === code; });
        if (!type)
            return code;
        return ((_a = intl.locale) !== null && _a !== void 0 ? _a : '').toLowerCase().startsWith('cs') ? type.labelCs : (type.labelEn || type.labelCs);
    };
    var threshold = function (rule) {
        var _a, _b, _c;
        switch (rule.thresholdStrategy) {
            case 'FREQUENCY':
                return msg('threshold.FREQUENCY', { value: (_a = rule.minFrequency) !== null && _a !== void 0 ? _a : '—' });
            case 'COVERAGE_PERCENTILE':
                return msg('threshold.COVERAGE_PERCENTILE', {
                    value: rule.coveragePercentile == null ? '—' : intl.formatNumber(rule.coveragePercentile * 100),
                });
            case 'EXPLICIT_LIST':
                return msg('threshold.EXPLICIT_LIST', { value: (_c = (_b = rule.whitelist) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0 });
            default:
                return rule.thresholdStrategy;
        }
    };
    var renderRules = function (rules) {
        if (rules.length === 0) {
            return React.createElement("p", { "data-testid": "import-pipeline-no-rules" }, msg('rules.empty'));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement("p", { "data-testid": "import-pipeline-rules-summary" }, msg('rules.summary', { running: rules.filter(runsAfterImport).length, total: rules.length })),
            React.createElement("table", { "data-testid": "import-pipeline-rules", style: { borderCollapse: 'collapse', width: '100%' } },
                React.createElement("thead", null,
                    React.createElement("tr", null, ['vocabulary', 'sources', 'threshold', 'enabled', 'autoOnImport', 'runsAfterImport'].map(function (col) { return (React.createElement("th", { key: col, scope: "col", style: CELL_STYLE }, msg("rules.column.".concat(col)))); }))),
                React.createElement("tbody", null, rules.map(function (rule) {
                    var _a;
                    return (React.createElement("tr", { key: rule.id, "data-testid": "import-pipeline-rule-".concat(rule.vocabularyType) },
                        React.createElement("td", { style: CELL_STYLE }, vocabularyLabel(rule.vocabularyType)),
                        React.createElement("td", { style: CELL_STYLE }, formatDerivationSources((_a = rule.sources) !== null && _a !== void 0 ? _a : [])),
                        React.createElement("td", { style: CELL_STYLE }, threshold(rule)),
                        React.createElement("td", { style: CELL_STYLE }, yesNo(rule.enabled)),
                        React.createElement("td", { style: CELL_STYLE, "data-testid": "import-pipeline-rule-auto-".concat(rule.vocabularyType) }, yesNo(rule.autoOnImport)),
                        React.createElement("td", { style: CELL_STYLE, "data-testid": "import-pipeline-rule-runs-".concat(rule.vocabularyType) },
                            React.createElement("strong", null, yesNo(runsAfterImport(rule))))));
                })))));
    };
    var renderPhase = function (phase, cap, rules) { return (React.createElement("li", { key: phase.code, "data-testid": "import-pipeline-phase-".concat(phase.code), style: { marginBottom: '0.75rem' } },
        React.createElement("strong", null, hasText("phase.".concat(phase.code, ".title")) ? msg("phase.".concat(phase.code, ".title")) : phase.code),
        hasText("phase.".concat(phase.code, ".text")) && (React.createElement("div", null, msg("phase.".concat(phase.code, ".text"), { cap: intl.formatNumber(cap) }))),
        phase.code === 'VOCABULARY_DERIVATION' && renderRules(rules))); };
    var renderBody = function () {
        if (isLoading)
            return React.createElement(Loading, null);
        if (isError || !data) {
            return React.createElement(MessageBanner, { type: "error" }, msg('loadError'));
        }
        return WAVES.map(function (wave) {
            var phases = data.phases.filter(function (p) { return p.wave === wave; });
            if (phases.length === 0)
                return null;
            return (React.createElement("section", { key: wave, "data-testid": "import-pipeline-wave-".concat(wave) },
                React.createElement("h3", null, msg("wave.".concat(wave, ".title"))),
                React.createElement("p", null, msg("wave.".concat(wave, ".text"))),
                React.createElement("ol", null, phases.map(function (p) { return renderPhase(p, data.touchedRecordCap, data.derivationRules); }))));
        });
    };
    return (React.createElement(Modal, { open: open, label: msg('title'), onClose: onClose, dismissible: true, size: "large", id: "import-pipeline-modal", footer: (React.createElement(ModalFooter, null,
            React.createElement(Button, { onClick: onClose, marginBottom0: true },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.pipeline.close" })))) }, renderBody()));
};
export default ImportPipelineModal;
